import React, { useState } from 'react'
import { connect } from 'react-redux'
import { Button, Collapse, Empty, Layout, Modal } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import TracpatchExerciseSettingsForm from '../../component/settings/tracpatch.exercise.settings.form'
import ProgressTestGoalsSettingsForm from '../../component/settings/progress.test.goals.settings.form'
import DefaultCarePlanSettingsForm from '../../component/settings/default.care.plan.settings.form'
import RemindersManager from '../../component/reminders/reminders.manager'
import { resetClinicToCompanyCarePlan } from '../../store/action/defaultcareplan.action'

const { Panel } = Collapse
const { Content } = Layout

function ClinicSettings(props) {
  const customerid = props.customer && props.customer._id
  const companyLabel = (props.company && props.company.name) || 'the company'
  // Controlled accordion: a section collapses once its form saves, so an
  // expanded panel signals a section that still has unsaved changes.
  const [activeKeys, setActiveKeys] = useState([])
  const [resetting, setResetting] = useState(false)
  const collapse = (key) => setActiveKeys((ks) => ks.filter((k) => k !== key))

  // The clinic's configured discipline (active surgery types) — drives the
  // "Applies to" options in the reminders editor, same as the old /events page.
  const clinicSurgeryTypes = (
    (props.customer && props.customer.surgerytypes) ||
    []
  )
    .filter((st) => st && st.active !== false && st.name)
    .map((st) => String(st.name).toLowerCase())

  // "Reset to company care plan": every panel on this page goes back to what
  // the company plan holds today — the state a clinic created now would start
  // in. Confirmed first; the panels collapse afterwards so each reloads fresh
  // from the server when reopened (destroyInactivePanel).
  const confirmReset = () => {
    Modal.confirm({
      title: `Reset this clinic to ${companyLabel}'s care plan?`,
      icon: <ExclamationCircleOutlined />,
      width: 520,
      content: (
        <div>
          <p>
            This replaces {props.customer.name || 'this clinic'}&apos;s own
            settings with the company care plan as it is today:
          </p>
          <ul style={{ paddingLeft: 20, marginBottom: 8 }}>
            <li>devices, exercises and questionnaires for each surgery type</li>
            <li>reminders</li>
            <li>TracPatch exercise settings</li>
            <li>Progress Test Goals</li>
          </ul>
          <p style={{ marginBottom: 0 }}>
            Every edit made on this page is lost. Patients already enrolled keep
            their current schedules. This cannot be undone.
          </p>
        </div>
      ),
      okText: 'Reset',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setResetting(true)
        try {
          const res = await props.resetClinicToCompanyCarePlan({ customerid })
          if (!res || !res.error) setActiveKeys([])
        } finally {
          setResetting(false)
        }
      },
    })
  }

  if (!customerid) {
    return (
      <Content style={{ padding: 24 }}>
        <Empty description='Select a clinic to manage its settings.' />
      </Content>
    )
  }

  return (
    <Content style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <h2 style={{ marginBottom: 0 }}>Clinic Settings</h2>
        <Button danger loading={resetting} onClick={confirmReset}>
          Reset to {companyLabel}&apos;s Care Plan
        </Button>
      </div>
      <div style={{ color: '#666', marginBottom: 16 }}>
        These overrides apply to every patient in {props.customer?.name || 'this clinic'}.
        Fields you don't override fall back to MoveAI's global defaults.
      </div>
      {/* destroyInactivePanel: a collapsed panel unmounts, so its form reloads
          fresh from the server on reopen — this is what makes each section's
          Cancel button discard unsaved edits (collapse = throw away drafts). */}
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys)}
        destroyInactivePanel
      >
        <Panel header="Clinic's Care Plan" key='default-care-plan'>
          <DefaultCarePlanSettingsForm
            customerid={customerid}
            onSaved={() => collapse('default-care-plan')}
            onCancel={() => collapse('default-care-plan')}
          />
        </Panel>
        <Panel header='Reminders' key='reminders'>
          <RemindersManager
            scope='clinic'
            customerid={customerid}
            surgeryTypes={clinicSurgeryTypes}
            companyName={props.company?.name}
            onCancel={() => collapse('reminders')}
          />
        </Panel>
        <Panel header='TracPatch Exercise Settings' key='tracpatch-exercises'>
          <TracpatchExerciseSettingsForm
            customerid={customerid}
            onSaved={() => collapse('tracpatch-exercises')}
            onCancel={() => collapse('tracpatch-exercises')}
          />
        </Panel>
        <Panel header='Progress Test Goals' key='progress-test-goals'>
          <ProgressTestGoalsSettingsForm
            customerid={customerid}
            onSaved={() => collapse('progress-test-goals')}
            onCancel={() => collapse('progress-test-goals')}
          />
        </Panel>
      </Collapse>
    </Content>
  )
}

const mapStateToProps = (state) => ({
  customer: state.profile.customer,
  company: state.profile.company,
})

const mapDispatchToProps = (dispatch) => ({
  resetClinicToCompanyCarePlan: (params) => dispatch(resetClinicToCompanyCarePlan(params)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ClinicSettings)
